import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Container, Grid,
  Typography, Button, Box, Select, MenuItem, FormControl, Chip,
  CircularProgress, TextField, InputAdornment, Paper, Snackbar, Alert, useMediaQuery, useTheme, Zoom, Fade, Badge
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import { useChat } from '../context/ChatContext';
import { useAuth } from '../context/AuthContext';
import ProductCard from '../components/ProductCard';
import CategoryFilterBar from '../components/CategoryFilterBar';
import { useParams, useNavigate } from 'react-router-dom';
import TablePersonDialog from '../components/TablePersonDialog';
import LimitInfoModal from '../components/LimitInfoModal';
import MultiplayerTicTacToe from '../components/MultiplayerTicTacToe';

const HomePage = ({ keywordsData, language }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { tableId } = useParams();
  const navigate = useNavigate();
  const loadingRef = useRef(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [deliverStatus, setDeliverStatus] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  const { 
    addToCart, snackbar, handleCloseSnackbar, setTableScan,
    setVipTableInfo, currentOrder, isOrder,
    resetTable, clearVipTableInfo, openLimitModel, setOpenLimitModel,
    getCartCount, cartItems
  } = useCart();

  const { registerTable } = useChat();
  
  const [firstLoad, setFirstLoad] = useState(true);
  const [personDialogOpen, setPersonDialogOpen] = useState(false);
  const [tableNumber, setTableNumber] = useState(null);
  const [selectedPro, setSelectedPro] = useState(null);
  const controllerRef = useRef(null);
  const [gameOpen, setGameOpen] = useState(false);
  const currentTableId = localStorage.getItem('tableId') || 0;

  // Calculate cart total items count
  const cartItemCount = getCartCount ? getCartCount() : (cartItems?.length || 0);
  
  // Calculate cart subtotal
  const cartSubtotal = cartItems?.reduce((sum, item) => sum + (item.final_price * item.quantity), 0) || 0;

  // Handle scroll to show/hide sticky bar
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > 100 && currentScrollY > lastScrollY) {
        setShowStickyBar(false);
      } else if (currentScrollY > 50) {
        setShowStickyBar(true);
      } else {
        setShowStickyBar(false);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleTableScan = (scannedTableNumber) => {
    setTableScan(scannedTableNumber);
    setTableNumber(scannedTableNumber);
    navigate('../');
    setPersonDialogOpen(true);
  };

  const handlePersonConfirm = (personCount) => {
    localStorage.setItem('table_persons', personCount);
    localStorage.setItem('tableId', tableNumber);
  };
  
  useEffect(() => {
    checkTableSession();
  }, []);
  
  // Fetch initial data
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    if (Number(tableId)) {
      handleTableScan(tableId);
    }
    const fetchInitialData = async () => {
      try {
        const [categoriesRes] = await Promise.all([
          api.get('/categories')
        ]);
        
        setCategories(categoriesRes.data);
        await fetchProducts(1, true);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [tableId]);

  const checkTableSession = () => {
    const tableIdS = localStorage.getItem('tableId');
    const tableStartTime = localStorage.getItem('tableStartTime');
    if (tableIdS && tableStartTime) {
      const elapsed = Date.now() - parseInt(tableStartTime);
      const THREE_HOURS = 3 * 60 * 60 * 1000;
      
      if (elapsed >= THREE_HOURS) {
        resetTable();
        return false;
      }
      registerTable(tableIdS);
    }
    return true;
  };

  const fetchProducts = async (page, reset = false, category = '', search = '') => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    const controller = new AbortController();
    controllerRef.current = controller;

    const setSpinner = reset ? setLoading : setLoadingMore;
    setSpinner(true);

    try {
      const params = new URLSearchParams({
        page,
        limit: 8,
        ...(category && { categoryId: category }),
        ...(search && { search }),
        lang: language
      });

      const { data } = await api.get(`/products?${params}`, {
        signal: controller.signal
      });

      setProducts(prev =>
        reset ? data.products : [...prev, ...data.products]
      );

      setDeliverStatus(data.deliver_status || false);
      setCurrentPage(data.pagination.currentPage);
      setTotalPages(data.pagination.totalPages);
      setHasMore(data.pagination.hasNext);

    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') {
        return;
      }
      console.error('Error fetching products:', err);
    } finally {
      setSpinner(false);
      setFirstLoad(false);
    }
  };

  const handleCategoryChange = (event) => {
    const categoryId = event.target.value;
    setSelectedCategory(categoryId);
    setCurrentPage(1);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    fetchProducts(1, true, categoryId, searchQuery);
  };
  
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSearchSubmit = async (event) => {
    if (event.key === 'Enter' || event.type === 'click') {
      setCurrentPage(1);
      await fetchProducts(1, true, selectedCategory, searchQuery);
    }
  };

  const handleSearchReset = async () => {
    setSearchQuery('');
    setCurrentPage(1);
    await fetchProducts(1, true, selectedCategory, '');
  };

  const handleScroll = useCallback(async () => {
    const scrollTop = document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = document.documentElement.clientHeight;
    
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      if (!loadingMore && hasMore) {
        var nextPage = 1;
        if (firstLoad) {
          setFirstLoad(false);
          nextPage = 1;
        } else {
          nextPage = currentPage + 1;
        }
        await fetchProducts(nextPage, false, selectedCategory, searchQuery);
      }
    }
  }, [currentPage, loadingMore, hasMore, selectedCategory, searchQuery, firstLoad]);

  useEffect(() => {
    let ticking = false;
    
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll);
    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
  }, [selectedCategory, searchQuery]);

  if (loading && products.length === 0) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress sx={{ color: '#FFD700' }} />
        <Typography>{language === 'ar' ? keywordsData.loading_txt_ar : keywordsData.loading_txt_en}</Typography>
      </Container>
    );
  }

  const queryTxt = language === 'ar' ? keywordsData.serach_query_product_txt_ar : keywordsData.serach_query_product_txt_en;

  return (
    <div>
      <CategoryFilterBar 
        categories={categories}
        selectedCategory={selectedCategory}
        handleCategoryChange={handleCategoryChange}
        language={language}
        variant="modern"
        showIcons={true}
        enableAnimation={true}
        scrollOnClick={true}
      />
      <Container sx={{ py: 4, mb: 10, pb: isOrder && isMobile ? 12 : 4 }}>
        {/* Search Box */}
        <Paper sx={{
          backgroundColor: '#0D0D0D',
          borderRadius: '30px',
          marginBottom: '24px',
          border: '1px solid #2D2D2D',
          '&:hover': { border: '1px solid #FFD700' },
          '&:focus-within': {
            border: '1px solid #FFD700',
            boxShadow: '0 0 0 2px rgba(255, 215, 0, 0.2)',
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', padding: '4px 16px' }}>
            <SearchIcon 
              sx={{ color: '#6B6B6B', cursor: 'pointer' }}
              onClick={handleSearchSubmit}
            />
            <input
              placeholder={language === 'ar' ? keywordsData.serach_product_txt_ar : keywordsData.serach_product_txt_en}
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyPress={handleSearchSubmit}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                padding: '14px 12px',
                width: '100%',
                color: '#FFFFFF',
                fontSize: '16px',
                outline: 'none',
                fontFamily: 'inherit',
              }}
            />
          </Box>
        </Paper>
        
        {products.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6">
              {language === 'ar' ? keywordsData.no_search_result_txt_ar : keywordsData.no_search_result_txt_en} {selectedCategory || searchQuery ? queryTxt : ''}
            </Typography>
            {(selectedCategory || searchQuery) && (
              <Button 
                variant="contained" 
                sx={{ mt: 2 }}
                onClick={handleSearchReset}
              >
                {language === 'ar' ? keywordsData.reset_search_txt_ar : keywordsData.reset_search_txt_en}
              </Button>
            )}
          </Box>
        ) : (
          <>
            <Grid container spacing={isMobile ? 1.5 : 2}>
              {products.map((product) => {
                return (
                  <ProductCard 
                    key={`${product.id}-${product.created_at}`}
                    product={product}
                    addToCart={addToCart}
                    keywordsData={keywordsData}
                    language={language}
                    deliverStatus={deliverStatus}
                    tableScan={localStorage.getItem('tableId') || 0}
                    setSelectedPro={(e) => setSelectedPro(e)}
                  />
                );
              })}
            </Grid>

            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>{language === 'ar' ? keywordsData.loading_search_txt_ar : keywordsData.loading_search_txt_en}</Typography>
              </Box>
            )}

            {!hasMore && products.length > 0 && (
              <Typography variant="body2" textAlign="center" sx={{ mt: 4, color: 'text.secondary' }}>
                {language === 'ar' ? keywordsData.no_more_search_txt_ar : keywordsData.no_more_search_txt_en}
              </Typography>
            )}
          </>
        )}

        {/* Modern Sticky Bar for Orders - Mobile Responsive */}
        {isOrder && isMobile && (
          <Fade in={showStickyBar} timeout={300}>
            <Paper
              elevation={10}
              sx={{
                position: 'fixed',
                bottom: 16,
                left: 16,
                right: 16,
                zIndex: 1200,
                backgroundColor: '#0D0D0D',
                backdropFilter: 'blur(10px)',
                background: 'linear-gradient(135deg, #0D0D0D 0%, #1A1A1A 100%)',
                border: '1px solid rgba(255, 215, 0, 0.3)',
                borderRadius: '20px',
                overflow: 'hidden',
                animation: 'slideUp 0.3s ease-out',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,215,0,0.1)',
              }}
            >
              <Box sx={{ p: 1.5 }}>
                {/* Order Info Header */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  mb: 1.5,
                  pb: 1,
                  borderBottom: '1px solid rgba(255,215,0,0.15)'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptLongIcon sx={{ color: '#FFD700', fontSize: 20 }} />
                    <Typography sx={{ 
                      color: '#FFD700', 
                      fontSize: '0.75rem', 
                      fontWeight: 600,
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase'
                    }}>
                      {language === 'ar' ? 'طلب حالي' : 'Current Order'}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={`Order #${currentOrder}`}
                    sx={{
                      backgroundColor: 'rgba(255,215,0,0.15)',
                      color: '#FFD700',
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      height: '22px',
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<RestaurantMenuIcon sx={{ fontSize: 18 }} />}
                    onClick={() => navigate(`/order-confirmation/${currentOrder}`)}
                    sx={{
                      borderColor: 'rgba(255,215,0,0.3)',
                      color: '#FFFFFF',
                      py: 1.2,
                      borderRadius: '14px',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#FFD700',
                        backgroundColor: 'rgba(255, 215, 0, 0.08)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    {language === 'ar' ? 'عرض الطلب' : 'View Order'}
                  </Button>
                  
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => setGameOpen(true)}
                    sx={{
                      backgroundColor: '#FFD700',
                      color: '#0D0D0D',
                      py: 1.2,
                      borderRadius: '14px',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      textTransform: 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#FFE55C',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(255,215,0,0.3)'
                      }
                    }}
                  >
                    🎮 {language === 'ar' ? 'العب XO' : 'Play Game'}
                  </Button>
                </Box>

                {/* Quick Stats */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  mt: 1.5,
                  pt: 1,
                  borderTop: '1px solid rgba(255,215,0,0.1)'
                }}>
                  <Typography sx={{ color: '#888888', fontSize: '0.6rem' }}>
                    {language === 'ar' ? 'متابعة الطلب' : 'Track Order'}
                  </Typography>
                  <ArrowForwardIcon sx={{ color: '#FFD700', fontSize: 14 }} />
                </Box>
              </Box>
            </Paper>
          </Fade>
        )}

        <style>
          {`
            @keyframes slideUp {
              from {
                opacity: 0;
                transform: translateY(30px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            
            @keyframes pulse {
              0% { transform: scale(0.9); opacity: 0; }
              100% { transform: scale(1); opacity: 1; }
            }
            
            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(30px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}
        </style>
        
        {/* Snackbar for status updates */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={1000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            sx={{
              backgroundColor: '#0D0D0D',
              color: '#FFFFFF',
              border: '1px solid #FFD700',
              borderRadius: '12px',
              fontWeight: 600,
              '& .MuiAlert-icon': {
                color: snackbar.severity === 'success' ? '#FFD700' : '#FF3D00',
              },
              '& .MuiAlert-action': {
                color: '#B3B3B3',
              }
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
        
        <TablePersonDialog
          open={personDialogOpen}
          onClose={() => {}}
          onConfirm={(personCount, tableInfo) => {
            console.log('Person count:', personCount);
            console.log('Table info:', tableInfo);
            clearVipTableInfo();
            localStorage.setItem('table_persons', personCount);
            localStorage.setItem('tableId', tableNumber);
            localStorage.setItem('tableStartTime', Date.now().toString());
            setPersonDialogOpen(false);
            registerTable(tableNumber);
            if (tableInfo.is_vip && tableInfo.min_charge > 0) {
              setVipTableInfo(tableInfo);
            }
          }}
          language={language}
          tableNumber={tableNumber}
          tableId={tableNumber}
          maxPersons={30}
          minPersons={1}
          disableBackdropClose={true}
          disableEscapeKeyClose={true}
        />
        
        <LimitInfoModal
          open={openLimitModel}
          onClose={() => setOpenLimitModel(false)}
          language={language}
          product={selectedPro}
        />
        
        <MultiplayerTicTacToe
          open={gameOpen}
          onClose={() => setGameOpen(false)}
          language={language}
          currentTableId={currentTableId || 0}
          currentTableName={currentTableId ? `Table ${currentTableId}` : 'Guest'}
          user={user}
        />
      </Container>
    </div>
  );
};

export default HomePage;